import { Injectable, signal, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Router } from '@angular/router';
import { TenantConfigService } from '../config/tenant-config';
import { environment } from '../../../environments/environment';

export interface ISendVerificationCodeResponse {
    response?: boolean;
    status?: boolean;
    message?: string;
    [key: string]: any;
}

export interface IValidateVerificationCodeResponse {
    response?: boolean;
    status?: boolean;
    message?: string;
    token?: string;
    [key: string]: any;
}

interface StoredAuthUser {
    token: string;
    email: string;
    lastActive: number;
}

const USER_STORAGE_KEY = 'user';
const ONE_HOUR_MS = 60 * 60 * 1000;

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    private http = inject(HttpClient);
    private router = inject(Router);
    private tenantConfig = inject(TenantConfigService);

    isLoginModalOpen = signal<boolean>(false);
    currentUser = signal<StoredAuthUser | null>(null);

    constructor() {
        this.initSession();
    }

    private getHeaders(): HttpHeaders {
        const websiteId = this.tenantConfig.settings?.['websiteId'];
        return new HttpHeaders({
            'Content-Type': 'application/json',
            'X-Tenant': websiteId ? websiteId.toString() : ''
        });
    }

    openLoginModal() {
        const token = this.getToken();
        if (token) {
            this.router.navigate(['/history']);
            return;
        }
        this.isLoginModalOpen.set(true);
    }

    closeLoginModal() {
        this.isLoginModalOpen.set(false);
    }

    sendVerificationCode(email: string): Observable<ISendVerificationCodeResponse> {
        return this.http.post<ISendVerificationCodeResponse>(
            `${environment.platformApiUrl}/send-verification-code`,
            { email },
            { headers: this.getHeaders() }
        );
    }

    validateVerificationCode(email: string, verificationCode: string): Observable<IValidateVerificationCodeResponse> {
        return this.http.post<IValidateVerificationCodeResponse>(
            `${environment.platformApiUrl}/validate-verification-code`,
            {
                email,
                verification_code: verificationCode
            },
            { headers: this.getHeaders() }
        );
    }

    saveSession(token: string, email: string) {
        const authData: StoredAuthUser = {
            token,
            email,
            lastActive: Date.now()
        };
        if (typeof localStorage !== 'undefined') {
            localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(authData));
        }
        this.currentUser.set(authData);
    }

    touchActivity() {
        const current = this.currentUser();
        if (current && typeof localStorage !== 'undefined') {
            const updated: StoredAuthUser = {
                ...current,
                lastActive: Date.now()
            };
            localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(updated));
            this.currentUser.set(updated);
        }
    }

    getToken(): string | null {
        if (this.isSessionExpired()) {
            this.logout();
            return null;
        }
        this.touchActivity();
        return this.currentUser()?.token ?? null;
    }

    logout() {
        if (typeof localStorage !== 'undefined') {
            localStorage.removeItem(USER_STORAGE_KEY);
        }
        this.currentUser.set(null);
    }

    isLoggedIn(): boolean {
        return !!this.getToken();
    }

    private initSession() {
        if (typeof localStorage === 'undefined') return;

        const raw = localStorage.getItem(USER_STORAGE_KEY);
        if (!raw) return;

        try {
            const parsed = JSON.parse(raw) as StoredAuthUser;
            const now = Date.now();
            if (now - (parsed.lastActive || 0) > ONE_HOUR_MS) {
                // Expired after 1 hour of unuse
                localStorage.removeItem(USER_STORAGE_KEY);
                this.currentUser.set(null);
            } else {
                this.currentUser.set(parsed);
                // Update activity upon restoring
                this.touchActivity();
            }
        } catch {
            localStorage.removeItem(USER_STORAGE_KEY);
            this.currentUser.set(null);
        }
    }

    private isSessionExpired(): boolean {
        const current = this.currentUser();
        if (!current) return true;
        const now = Date.now();
        return (now - (current.lastActive || 0)) > ONE_HOUR_MS;
    }
}
